/**
 * Inventory Sync — admin
 *
 * The screen exists because a stock import (STO01, POST /api/offers/stock/imports) is
 * ASYNCHRONOUS. It answers 201 with an import_id and nothing more; the quantity may be
 * applied seconds later, or rejected with no further notice. So this screen never says
 * "synced" off the back of a push. An import is `applied` only once STO02 reports COMPLETE
 * with zero lines in error — everything else is queued, partial, failed or stale, shown
 * separately because each needs a different response.
 *
 * The state that costs money is `partial`: STO02 says COMPLETE, but a line was rejected,
 * so the retailer is still selling stock we no longer have.
 *
 * The rows are stock imports throughout: SKU and quantity, nothing else. The ledger behind
 * them is still called MiraklOfferImport because the push used to go through OF24, which
 * replaced the whole offer — which is exactly why nothing on this screen calls them offers.
 */

import React, { useEffect, useState } from 'react';
import {
  Page, Layout, Card, Text, Box, BlockStack, InlineStack, InlineGrid, Badge, Button,
  IndexTable, Spinner, Banner, Select, Tooltip, Divider, Modal, Pagination,
} from '@shopify/polaris';
import { useInventorySyncSummary, useOfferImportErrorReport } from '../utils/queries';
import { headingsFor, emptyRow, DEFAULT_PAGE_SIZE, PageSizeSelect } from '../utils/table';

const PERIODS = [
  { label: 'Last 7 days', value: '7' },
  { label: 'Last 30 days', value: '30' },
  { label: 'Last 90 days', value: '90' },
];

/**
 * Column definitions for the two tables on this screen, with a tip each.
 *
 * Both used to be replaced by a sentence when empty, so on a quiet connector the whole
 * page read as prose with no structure and no sign of what would appear. See utils/table.jsx
 * for why IndexTable cannot do this by itself.
 *
 * The tips are not decoration: "SKU" is only ever populated for a single-SKU import, so
 * without one the column reads as missing data on nearly every row.
 */
const IMPORT_COLUMNS = [
  { key: 'import', title: 'Import', tip: "The retailer's own id for the inventory file we submitted. Everything else on the row is resolved by polling it." },
  { key: 'outcome', title: 'Outcome', tip: 'What the retailer has reported so far. Applied is the only state that means the quantity is live at the retailer; hover the badge itself for what each one means.' },
  { key: 'sku', title: 'SKU', tip: 'The SKU, when the import carried exactly one. A batch shows how many it carried instead — open Details to see them. "Not recorded" means the SKU-level records are gone, which is not the same as none.' },
  { key: 'qty', title: 'Qty pushed', tip: 'What we submitted, not what the retailer now holds — those differ until the import resolves, and stay different for any line the retailer rejected. A batch has one quantity per SKU and no meaningful total.' },
  { key: 'brand', title: 'Brand', tip: 'Every brand whose SKUs were in this import. Imports are built per retailer ACCOUNT and several brands share an account, so one import can legitimately cover several.' },
  { key: 'retailer', title: 'Retailer', tip: 'The retailer account the file was submitted to.' },
  { key: 'accepted', title: 'SKUs accepted', tip: "Accepted out of submitted, as the retailer's own import result reports it. A shortfall here is stock still wrong at the retailer." },
  { key: 'submitted', title: 'Submitted', tip: 'When we sent the file. The retailer applies it asynchronously, so this is not when the stock changed.' },
  { key: 'open', title: 'Open for', tip: 'How long the import has been unresolved. Minutes is normal; days means the poller has stopped or the retailer never answered.' },
  { key: 'details', title: 'Details', tip: 'Opens what the import carried, and the retailer’s rejection report where there is one.' },
];

const REJECTION_COLUMNS = [
  { key: 'import', title: 'Import', tip: "The retailer's own id for the file these rejections came from." },
  { key: 'outcome', title: 'Outcome', tip: 'Partially rejected means the file landed but individual lines were refused. Failed means none of it applied.' },
  { key: 'sku', title: 'SKU', tip: 'The SKU, where the import carried only one. For a batch the per-SKU detail is in the retailer’s rejection report — open Details on the row above.' },
  { key: 'retailer', title: 'Retailer', tip: 'The retailer account that refused it.' },
  { key: 'reason', title: 'Reason', tip: "The retailer's own reason, or the first line of its rejection report. Absent means the retailer offered no reason, not that there wasn't one." },
  { key: 'when', title: 'When', tip: 'When the retailer finished with the import, or when we submitted it if it never resolved.' },
];

/** One place decides how each outcome reads, so the table and the cards cannot drift. */
const OUTCOME = {
  applied: { label: 'Applied', tone: 'success', help: "The retailer's import result came back complete with no line in error — the quantity is live at the retailer." },
  partial: { label: 'Partially rejected', tone: 'warning', help: "The retailer's import result came back complete, but one or more lines were rejected. The retailer may still be selling stock we no longer have." },
  failed: { label: 'Failed', tone: 'critical', help: "The retailer's import result came back failed. The quantity never moved." },
  queued: { label: 'Queued at Mirakl', tone: 'attention', help: "The inventory push was accepted, but the retailer has not reported an import result yet. Normal for the first few minutes." },
  stale: { label: 'Stale', tone: 'critical', help: 'Open for more than 7 days. The poller has abandoned it — it will never resolve on its own.' },
  unknown: { label: 'Unknown', tone: undefined, help: 'Mirakl returned a status we do not recognise.' },
};

/**
 * Rows-per-page and page number for ONE table.
 *
 * Both tables on this screen get their own, so widening one to read it does not resize the
 * other out from under whoever was reading that.
 *
 * The reset matches QueueTable's: a new row count, or a new PERIOD (the only filter here),
 * starts again at page 1 — otherwise narrowing from 90 days to 7 while on page 5 renders an
 * empty slice, which reads as "no imports" rather than "wrong page".
 */
const usePaging = (seed, resetKey) => {
  const [pageSize, setPageSize] = useState(seed);
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [pageSize, resetKey]);
  return { page, setPage, pageSize, setPageSize };
};

/**
 * The slice a table actually renders, with the page CLAMPED to what exists.
 *
 * Every table here is paged CLIENT-SIDE: GET /inventory-sync/summary takes only `days` and
 * returns both lists whole (the server's own caps — 100 imports, 50 rejections — are
 * disclosed beside the headings, not paged with), so a size change costs no request. The
 * clamp matters because this screen refetches on a period change and the lists shrink: an
 * out-of-range slice is empty, and would render the "nothing in this period" row over a
 * ledger that still has imports in it.
 */
const windowOf = (rows, { page, pageSize }) => {
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  return {
    totalPages,
    safePage,
    visible: rows.slice((safePage - 1) * pageSize, safePage * pageSize),
  };
};

/**
 * Both controls in one row under a table, and only once it has rows — a pager under an empty
 * table is three dead words. The pager drops out when everything fits; the row count stays,
 * since it is how anyone gets a second page at all.
 */
const tableFooter = (total, paging, win) => (total === 0 ? null : (
  <Box padding="300">
    <InlineStack align="space-between" blockAlign="center">
      <PageSizeSelect value={paging.pageSize} onChange={paging.setPageSize} />
      {win.totalPages > 1 && (
        /* `safePage`, the page the rows were actually sliced with — the raw page would read
           "Page 5 of 2" for as long as the shorter period stayed selected. */
        <Pagination
          hasPrevious={win.safePage > 1}
          onPrevious={() => paging.setPage(win.safePage - 1)}
          hasNext={win.safePage < win.totalPages}
          onNext={() => paging.setPage(win.safePage + 1)}
          label={`Page ${win.safePage} of ${win.totalPages}`}
        />
      )}
    </InlineStack>
  </Box>
));

const when = (d) => (d ? new Date(d).toLocaleString() : '—');
const mins = (m) => (m == null ? '—' : m < 60 ? `${m}m` : m < 1440 ? `${Math.floor(m / 60)}h` : `${Math.floor(m / 1440)}d`);

export default function InventorySync() {
  const [period, setPeriod] = useState('30');
  const [reportFor, setReportFor] = useState(null);

  const { data, isLoading, error } = useInventorySyncSummary({ days: period });
  const { data: report, isLoading: reportLoading } = useOfferImportErrorReport(reportFor?.id);

  // One per table, so no two move together. Every one resets on a period change.
  const importPaging = usePaging(DEFAULT_PAGE_SIZE, period);
  const rejectionPaging = usePaging(DEFAULT_PAGE_SIZE, period);

  const p = data?.pipeline ?? {};
  // Period-wide rejection total, for the Rejections badge (the rejections LIST is capped at 50).
  const rejectionTotal = (Number(p.partial) || 0) + (Number(p.failed) || 0);
  const imports = data?.imports ?? [];
  const rejections = data?.rejections ?? [];

  // The window each table renders. The counts and "showing X of Y" lines above the tables
  // deliberately keep measuring the FULL arrays — they describe what was fetched against what
  // exists, which is a different statement from which page is on screen.
  const importWin = windowOf(imports, importPaging);
  const rejectionWin = windowOf(rejections, rejectionPaging);

  if (error) {
    return (
      <Page fullWidth>
        <Layout><Layout.Section>
          <Banner tone="critical">Unable to load inventory sync data: {error.message}</Banner>
        </Layout.Section></Layout>
      </Page>
    );
  }

  const stat = (label, value, tone, sub, tip) => (
    <Card>
      <Box padding="300">
        <BlockStack gap="050">
          <InlineStack gap="100" blockAlign="center">
            <Text variant="bodySm" tone="subdued">{label}</Text>
            {tip && <Tooltip content={tip}><Text variant="bodySm" tone="subdued">ⓘ</Text></Tooltip>}
          </InlineStack>
          <Text variant="heading2xl" tone={tone}>{isLoading ? '—' : value ?? 0}</Text>
          {sub && <Text variant="bodySm" tone="subdued">{sub}</Text>}
        </BlockStack>
      </Box>
    </Card>
  );

  const outcomeBadge = (outcome) => {
    const o = OUTCOME[outcome] ?? OUTCOME.unknown;
    return <Tooltip content={o.help}><Badge tone={o.tone}>{o.label}</Badge></Tooltip>;
  };

  /**
   * The Brand cell, which must be able to say "several".
   *
   * A stock import is built per RETAILER ACCOUNT, and several 2W brands routinely share
   * one Mirakl account, so an import can cover more than one brand. This cell used to
   * render `r.brandName` — the ledger's single column — which named whichever brand owned
   * the batch's oldest queued SKU and silently spoke for all the others. A partial
   * rejection therefore sent the admin after the wrong brand.
   *
   * `r.brands` now comes from the push rows that made up the import, so it is the measured
   * set. `brandsFromPushRows` false means those rows are gone (pruned, or the import
   * pre-dates this fix) and the name is only the ledger's claim — said as a claim.
   */
  const brandCell = (r) => {
    const list = r.brands ?? [];

    if (list.length === 0) {
      return <Text variant="bodySm" tone="subdued">—</Text>;
    }

    if (list.length === 1) {
      const only = list[0].name;
      if (r.brandsFromPushRows) return <Text variant="bodySm">{only}</Text>;
      return (
        <Tooltip content={
          'The SKU-level records for this import are no longer available, so this is the brand '
          + 'the import was filed under rather than a brand confirmed from its contents. An '
          + 'import can cover several brands on a shared retailer account.'
        }>
          <Text variant="bodySm" tone="subdued">{only} (unconfirmed)</Text>
        </Tooltip>
      );
    }

    // Named in the tooltip rather than the cell: this column is one of nine and a
    // three-brand list would set the row height for the whole table.
    return (
      <Tooltip content={`This import covered ${list.length} brands: ${list.map((b) => b.name).join(', ')}.`}>
        <Text variant="bodySm">{list.length} brands</Text>
      </Tooltip>
    );
  };

  /**
   * The SKU cell, which must distinguish "many" from "we do not know".
   *
   * MiraklOfferImport.shopSku is written only when a batch happens to hold exactly ONE
   * SKU (inventory-sync-service.js: `included.length === 1 ? ... : null`). Batching is the
   * entire point of a stock import, so in practice this column was a dash on almost every
   * row — and a dash cannot distinguish "this import carried 40 SKUs" from "the record is
   * gone". Those need different responses, so they now read differently.
   *
   * skuCount comes from the import's push rows: a number means measured, null means those
   * rows are no longer there (pruned, or the import pre-dates the backend fix).
   */
  const skuCell = (r) => {
    if (r.skuCount === 1 && (r.skus?.length ?? 0) === 1) {
      return <Text variant="bodySm">{r.skus[0].shopSku}</Text>;
    }
    // Single-SKU import recorded before push rows carried the detail: the ledger's own
    // column is still the best answer, and it is a real SKU rather than a guess.
    if (r.skuCount == null && r.shopSku) {
      return <Text variant="bodySm">{r.shopSku}</Text>;
    }
    if (r.skuCount == null) {
      return (
        <Tooltip content={
          'The SKU-level records for this import are no longer available, so what it carried '
          + 'cannot be listed. This is not the same as an import with no SKUs.'
        }>
          <Text variant="bodySm" tone="subdued">not recorded</Text>
        </Tooltip>
      );
    }
    if (r.skuCount === 0) {
      return <Text variant="bodySm" tone="subdued">none</Text>;
    }
    // Many. A button, not text, because the list is worth opening and there was previously
    // no way to see it at all unless the import happened to have failed.
    return (
      <Button variant="plain" size="micro" onClick={() => setReportFor(r)}>
        {`${r.skuCount} SKUs`}
      </Button>
    );
  };

  /**
   * Qty pushed. Meaningful only for a single-SKU import — a batch has one quantity per
   * SKU and no total worth showing, so it points at the list instead of inventing a sum.
   */
  const qtyCell = (r) => {
    if (r.skuCount === 1 && (r.skus?.length ?? 0) === 1 && r.skus[0].quantity != null) {
      return <Text variant="bodySm">{r.skus[0].quantity}</Text>;
    }
    if (r.pushedQuantity != null) return <Text variant="bodySm">{r.pushedQuantity}</Text>;
    if ((r.skuCount ?? 0) > 1) {
      return (
        <Tooltip content="One quantity per SKU in this import. Open the SKU list to see them.">
          <Text variant="bodySm" tone="subdued">per SKU</Text>
        </Tooltip>
      );
    }
    return <Text variant="bodySm" tone="subdued">—</Text>;
  };

  // The rate is deliberately absent, not zero, until something has resolved.
  const rate = p.appliedRate == null
    ? { value: '—', sub: p.appliedRateReason ?? 'not yet computable' }
    : { value: `${p.appliedRate}%`, sub: `of ${(p.applied ?? 0) + (p.partial ?? 0) + (p.failed ?? 0)} resolved imports` };

  return (
    <Page fullWidth>
      <BlockStack gap="400">

        <InlineStack align="space-between" blockAlign="center">
          <BlockStack gap="050">
            <Text variant="headingLg" as="h1">Inventory Sync</Text>
            <Text variant="bodySm" tone="subdued">
              Inventory pushes to retailers, resolved through each retailer&rsquo;s import result
            </Text>
          </BlockStack>
          <Select label="" labelHidden options={PERIODS} value={period} onChange={setPeriod} />
        </InlineStack>

        {/* Anything that needs an admin now, stated before the detail. */}
        {!isLoading && (p.stale > 0 || p.failed > 0 || p.partial > 0) && (
          <Banner tone={p.stale > 0 || p.failed > 0 ? 'critical' : 'warning'}>
            <BlockStack gap="100">
              {p.stale > 0 && (
                <Text variant="bodySm">
                  <strong>{p.stale} import{p.stale === 1 ? '' : 's'} stale.</strong> Open more than 7 days and no longer polled —
                  the quantity never reached the retailer and nothing will retry it.
                </Text>
              )}
              {p.failed > 0 && (
                <Text variant="bodySm">
                  <strong>{p.failed} import{p.failed === 1 ? '' : 's'} failed.</strong> Mirakl rejected the file outright.
                </Text>
              )}
              {p.partial > 0 && (
                <Text variant="bodySm">
                  <strong>{p.partial} partially rejected.</strong> Reported COMPLETE, but individual lines were refused —
                  those SKUs are still listed at the old quantity.
                </Text>
              )}
            </BlockStack>
          </Banner>
        )}

        {/* ---- pipeline ------------------------------------------------------ */}
        <BlockStack gap="200">
          <Text variant="bodySm" tone="subdued">IMPORT PIPELINE</Text>
          <InlineGrid columns={{ xs: 1, sm: 2, md: 3, lg: 6 }} gap="300">
            {stat('Submitted', p.submitted, undefined, 'push accepted', 'An accepted inventory push means the retailer took the file — not that the quantity changed.')}
            {stat('Queued at Mirakl', p.queued, p.queued > 0 ? 'caution' : undefined, 'awaiting import result', OUTCOME.queued.help)}
            {stat('Applied', p.applied, 'success', 'live at retailer', OUTCOME.applied.help)}
            {stat('Partially rejected', p.partial, p.partial > 0 ? 'caution' : undefined, 'lines refused', OUTCOME.partial.help)}
            {stat('Failed', p.failed, p.failed > 0 ? 'critical' : undefined, 'never applied', OUTCOME.failed.help)}
            {stat('Stale', p.stale, p.stale > 0 ? 'critical' : undefined, 'abandoned', OUTCOME.stale.help)}
          </InlineGrid>
          {/* SKUs mapped and Connections stood beside Applied rate. Both are catalogue
              facts rather than import outcomes, and Retailer Accounts already owns them: the
              SKU Map table counts the SKU-retailer pairs, and the Brands table says
              WHICH brands are connected, which is more use than how many. Grid left at three
              columns so Applied rate keeps its width under the pipeline row above. */}
          <InlineGrid columns={{ xs: 1, sm: 2, md: 3 }} gap="300">
            {stat('Applied rate', rate.value, undefined, rate.sub, 'Computed over resolved imports only. Imports still queued are excluded so the figure is neither flattered nor unfairly damned.')}
          </InlineGrid>
        </BlockStack>

        {/* ---- ledger -------------------------------------------------------- */}
        <Card>
          <Box padding="300">
            <InlineStack align="space-between" blockAlign="center">
              <Text variant="headingMd" as="h3">Stock imports</Text>
              {/* Derived from the rows actually received, not from a hardcoded 100. The 100 was
                  the server's own `imports: shaped.slice(0, 100)` cap
                  (inventory-sync.controller.js:211-212) copied into the UI, so if that cap ever
                  moved this line would state a number that was never true. */}
              {data?.truncated > 0 && (
                <Text variant="bodySm" tone="subdued">
                  showing {imports.length} of {imports.length + data.truncated}
                </Text>
              )}
            </InlineStack>
          </Box>
          {isLoading ? (
            <Box padding="400"><Spinner size="small" /></Box>
          ) : (
            <>
            <IndexTable
              resourceName={{ singular: 'import', plural: 'imports' }}
              // Counts the slice on screen, not the whole ledger: itemCount has to follow the
              // array actually rendered, and `|| 1` keeps the headings when that array is
              // empty (IndexTable swaps the whole table, headings included, at itemCount 0).
              itemCount={importWin.visible.length || 1}
              selectable={false}
              // "SKUs accepted" keeps Mirakl's meaning but not its word: the client asked
              // for SKUs throughout, and it is not plain "SKUs" because a column three to
              // the left is already "SKU". The STO02 help text below still says "lines",
              // correctly — that is quoting the retailer's own error report.
              headings={headingsFor(IMPORT_COLUMNS)}
            >
              {importWin.visible.length === 0 ? emptyRow(
                IMPORT_COLUMNS, 'imports',
                error
                  ? 'Could not load stock imports — see the error above.'
                  : 'No stock imports in this period. A Shopify stock change only QUEUES a push — the row here is created by the scheduled push that follows, so an empty ledger means no stock moved, the scheduled inventory push is switched off in Sync Settings, or the connector is not running.',
              ) : importWin.visible.map((r, i) => (
                <IndexTable.Row id={r.id} key={r.id} position={i}>
                  <IndexTable.Cell><Text variant="bodySm" fontWeight="semibold">#{r.importId}</Text></IndexTable.Cell>
                  <IndexTable.Cell>{outcomeBadge(r.outcome)}</IndexTable.Cell>
                  <IndexTable.Cell>{skuCell(r)}</IndexTable.Cell>
                  <IndexTable.Cell>{qtyCell(r)}</IndexTable.Cell>
                  <IndexTable.Cell>{brandCell(r)}</IndexTable.Cell>
                  <IndexTable.Cell><Text variant="bodySm">{r.retailer}</Text></IndexTable.Cell>
                  <IndexTable.Cell>
                    <Text variant="bodySm" tone={r.linesInError > 0 ? 'critical' : undefined}>
                      {r.linesRead == null ? '—' : `${r.linesInSuccess ?? 0}/${r.linesRead}`}
                      {r.linesInError > 0 ? ` · ${r.linesInError} rejected` : ''}
                    </Text>
                  </IndexTable.Cell>
                  <IndexTable.Cell><Text variant="bodySm" tone="subdued">{when(r.submittedAt)}</Text></IndexTable.Cell>
                  <IndexTable.Cell>
                    <Text variant="bodySm" tone={r.outcome === 'stale' ? 'critical' : 'subdued'}>
                      {r.completedAt ? '—' : mins(r.openMinutes)}
                    </Text>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    {/*
                      Available on EVERY row, not only on one with an error report.
                      This was `r.hasErrorReport && <Button>Why?</Button>`, so a successful
                      import — or a rejected one Mirakl gave no STO03 report for — had no
                      action at all, and its contents could not be seen from anywhere on the page.
                      The modal now carries both what the import held and why lines were
                      refused, so the label says "Details" rather than "Why?": on most rows
                      there is nothing to ask why about, and the answer is still one click
                      away on the rows where there is.
                    */}
                    <Button size="micro" onClick={() => setReportFor(r)}>
                      {r.hasErrorReport ? 'Details · why' : 'Details'}
                    </Button>
                  </IndexTable.Cell>
                </IndexTable.Row>
              ))}
            </IndexTable>
            {tableFooter(imports.length, importPaging, importWin)}
            </>
          )}
        </Card>

        {/* ---- rejections ---------------------------------------------------- */}
        {/* Rendered unconditionally now. This card used to exist only when there WAS a
            rejection, so on a healthy connector the screen gave no hint that rejections are
            tracked at all — and an admin could not tell "none" from "not measured". */}
        {(
          <Card>
            <Box padding="300">
              <InlineStack align="space-between" blockAlign="center">
                <Text variant="headingMd" as="h3">Rejections</Text>
                {/* `rejections` is capped at 50 server-side
                    (inventory-sync.controller.js:185-188 `.slice(0, 50)`), so this badge froze at
                    "50 imports" once there were more. The true period total is
                    pipeline.partial + pipeline.failed, from the same payload - which is already
                    what the banner above uses. Show both when the list is truncated. */}
                <Badge tone="critical">
                  {rejectionTotal > rejections.length
                    ? `${rejections.length} of ${rejectionTotal} imports`
                    : `${rejections.length} import${rejections.length === 1 ? '' : 's'}`}
                </Badge>
              </InlineStack>
              <Text variant="bodySm" tone="subdued">
                Every one of these leaves a SKU listed at a quantity we did not intend.
              </Text>
            </Box>
            <IndexTable
              resourceName={{ singular: 'rejection', plural: 'rejections' }}
              itemCount={rejectionWin.visible.length || 1}
              selectable={false}
              headings={headingsFor(REJECTION_COLUMNS)}
            >
              {rejectionWin.visible.length === 0 ? emptyRow(
                REJECTION_COLUMNS, 'rejections',
                error
                  ? 'Could not load rejections — see the error above.'
                  : 'No rejection in this period. Every import the retailer finished with was applied in full.',
              ) : rejectionWin.visible.map((r, i) => (
                <IndexTable.Row id={`rej-${r.id}`} key={`rej-${r.id}`} position={i}>
                  <IndexTable.Cell><Text variant="bodySm" fontWeight="semibold">#{r.importId}</Text></IndexTable.Cell>
                  <IndexTable.Cell>{outcomeBadge(r.outcome)}</IndexTable.Cell>
                  <IndexTable.Cell><Text variant="bodySm">{r.shopSku ?? '—'}</Text></IndexTable.Cell>
                  <IndexTable.Cell><Text variant="bodySm">{r.retailer}</Text></IndexTable.Cell>
                  <IndexTable.Cell>
                    <Text variant="bodySm" tone="critical">
                      {r.reasonStatus || (r.errorReport ? r.errorReport.split('\n')[0] : 'Mirakl gave no reason')}
                    </Text>
                  </IndexTable.Cell>
                  <IndexTable.Cell><Text variant="bodySm" tone="subdued">{when(r.completedAt || r.submittedAt)}</Text></IndexTable.Cell>
                </IndexTable.Row>
              ))}
            </IndexTable>
            {/* The badge above still states the period-wide total against the fetched list;
                this only pages the list, so the two cannot come to disagree. */}
            {tableFooter(rejections.length, rejectionPaging, rejectionWin)}
          </Card>
        )}

        {/* Brand sync health and Recent inventory jobs stood here, side by side.
            Health's Imports/Queued/Rejected were the import-pipeline tiles above split per
            connection, and its Jobs/Last run columns are Background Jobs -> Sync History's
            subject. The jobs card listed the same MiraklSyncJob INVENTORY_SYNC rows as that
            page, but headed its message column "Errors" and rendered it red: a note from a
            run that COMPLETED having done nothing therefore read as a failure.
            run-health-service.js and brand-runs-service.js both split those on
            status === 'COMPLETED'; this card never did, and its Retailer column said
            "all retailers" on every row. */}

        <Divider />
        <Box paddingBlockEnd="400">
          <Text variant="bodySm" tone="subdued">
            An inventory push is accepted asynchronously and returns only an import id. Status is resolved by
            polling the retailer&rsquo;s import result every 15 seconds — the retailer&rsquo;s own published
            ceiling for that check.
            An import still queued minutes after submission is normal; one queued for days is not.
          </Text>
        </Box>
      </BlockStack>

      {/*
        Import detail. Two sections, because an admin arrives here with two different
        questions and previously could only ask one of them.

        WHAT WAS IN IT is local and always available: it comes from the summary payload
        already loaded, so it renders with no fetch and works for a SUCCESSFUL import too.
        Before this, the modal opened only from a "Why?" button that existed solely when
        Mirakl had returned an error report — so there was no way to see the contents of an
        import that worked, and no way to see them at all for one that had no STO03 report.

        WHY IT WAS REJECTED is the STO03 report, still fetched only when opened.
      */}
      <Modal
        open={Boolean(reportFor)}
        onClose={() => setReportFor(null)}
        title={reportFor ? `Import #${reportFor.importId}` : ''}
        secondaryActions={[{ content: 'Close', onAction: () => setReportFor(null) }]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <BlockStack gap="100">
              <Text variant="headingSm" as="h3">What this import carried</Text>
              {reportFor?.skuCount == null ? (
                <Text variant="bodySm" tone="subdued">
                  The SKU-level records for this import are no longer available, so its contents
                  cannot be listed.
                </Text>
              ) : (
                <>
                  <Text variant="bodySm" tone="subdued">
                    {reportFor.skuCount} SKU{reportFor.skuCount === 1 ? '' : 's'}
                    {(reportFor.brands?.length ?? 0) > 1
                      ? `, across ${reportFor.brands.length} brands (${reportFor.brands.map((b) => b.name).join(', ')})`
                      : ''}
                    {reportFor.skusTruncated > 0
                      ? ` — showing the first ${reportFor.skus.length}`
                      : ''}
                  </Text>
                  <Box padding="200" background="bg-surface-secondary" borderRadius="200">
                    <BlockStack gap="050">
                      {(reportFor.skus ?? []).map((s) => (
                        <InlineStack key={s.shopSku} align="space-between">
                          <Text variant="bodySm">{s.shopSku}</Text>
                          <Text variant="bodySm" tone="subdued">
                            {s.quantity == null ? '—' : `qty ${s.quantity}`}
                          </Text>
                        </InlineStack>
                      ))}
                    </BlockStack>
                  </Box>
                  {/* Said once, here, where someone is looking at the numbers: the
                      quantity is what we SUBMITTED, not what the retailer now holds. */}
                  <Text variant="bodySm" tone="subdued">
                    Quantities are what we submitted. Whether each one was applied is the
                    import&rsquo;s outcome above, and per-SKU refusals appear below when the
                    retailer reports them.
                  </Text>
                </>
              )}
            </BlockStack>

            <Divider />

            <BlockStack gap="100">
              <Text variant="headingSm" as="h3">Why lines were rejected</Text>
              {!reportFor?.hasErrorReport ? (
                <Text variant="bodySm" tone="subdued">
                  The retailer returned no rejection report for this import.
                </Text>
              ) : reportLoading ? (
                <Spinner size="small" />
              ) : report?.errorReport ? (
                <BlockStack gap="200">
                  <Text variant="bodySm" tone="subdued">
                    From the retailer&rsquo;s own rejection report
                    {report.shopSku ? `, for SKU ${report.shopSku}` : ''}.
                  </Text>
                  <Box padding="300" background="bg-surface-secondary" borderRadius="200" overflowX="scroll">
                    <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>{report.errorReport}</pre>
                  </Box>
                </BlockStack>
              ) : (
                <Text variant="bodySm" tone="subdued">{report?.reason ?? 'No error report available.'}</Text>
              )}
            </BlockStack>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
